// Module 07 (engine) collaborator — tick step 9: assemble the immutable per-tick Snapshot. Owns the pure
// derivations (outages, bus/branch angle maxima, nodeAngles, genMw, dark-load → unserved) + the totals{}
// object. Extracted VERBATIM from TsEngimpl behind a byte-stable createEngine (P10): golden-NEUTRAL. Takes a
// single NAMED-OBJECT arg so a mis-wired field is a typecheck error, never a silent regression. The static
// graph (nodes/edges/nodeIndex) is captured at construction; everything else is passed per tick. The engine
// keeps prevEnergized (tick bookkeeping). Same-module file: imports domain + the powerflow public entry.
import type {
  GridNode, GridEdge, NodeId, EdgeId, Snapshot, BranchFlow, FlowSample,
  Scenario, ProsumerRuntime, LoadRuntime, Fault, InvariantReport,
} from "../domain/index";
import type { DispatchResult, FreqResult } from "../powerflow/index";

const RAD2DEG = 180 / Math.PI;
// engine.v3 P7: undervoltage/sag threshold. Grounded in ANSI C84.1 Range A service voltage (±5% ⇒ 0.95–1.05
// pu); a bus at/below 0.95 pu is a detected sag. CSA CAN3-C235 is directionally consistent. Research-verified.
const SAG_PU = 0.95;

export interface AssembleArgs {
  tickCount: number;
  scenario: Scenario;
  price: Snapshot["price"];
  dr: DispatchResult;
  freq: FreqResult;
  theta: Float64Array;
  branchFlows: BranchFlow[];
  energyFlows: readonly FlowSample[];
  // engine.v3 P7 (domain-v7): per-bus squared voltage magnitude w=|V|² (LinDistFlow), node-array order.
  // √w → busVMagPu; min/max/sag scalars ride totals. 1.0 flat on the golden (rPu=0 ⇒ no resistive drop).
  voltageSq: Float64Array;
  overlayFlows: readonly FlowSample[];
  financeFlows: readonly FlowSample[];
  energized: ReadonlySet<NodeId>;
  openEdges: ReadonlySet<EdgeId>;
  prosumers: ProsumerRuntime[];
  loads: LoadRuntime[];
  grossDemandMw: number;
  totalNetLoadMw: number;
  lossMw: number;
  // engine.v3 P11 (C31): distribution / last-mile loss + combined T&D total (always-emitted; 0 / lossMw default).
  distLossMw?: number | undefined;
  totalTndLossMw?: number | undefined;
  // engine.v3 P12 (C22): protection coordination — locked-out branch count + un-trippable count (always 0 on N-0).
  lockedOutCount?: number | undefined;
  protectionUntripCount?: number | undefined;
  // engine.v3 P0-harness (R2): the per-tick physics self-check diagnostics (read-only; golden-neutral).
  conservationResidualMw?: number | undefined; // Σ P[i] over all buses (must be ~0)
  slackAbsorbedMw?: number | undefined; // −Σ_{i≠slack} P[i] (the derived reference-bus residual)
  // engine.v3 P2 (domain-v5): the FULL per-tick physics self-check report (6 families; totals.invariant).
  invariant?: InvariantReport | undefined;
  // engine.v3 P5 (domain-v6): live MSSC (reserve-sizing) + SCED binding diagnostics.
  largestContingencyMw?: number | undefined;
  bindingCorridors?: import("../domain/index").CorridorId[] | undefined;
  bindingBranchCount?: number | undefined;
  nodalPrices?: Snapshot["nodalPrices"]; // engine.v3 F4b (P13): per-bus LMP decomposition (gated on congestion)
  // engine.v3 P8 (env→infrastructure): this tick's DLR/R(T) factors + the derate/iron-loss MW. Always emitted.
  lineRatingFactor?: number | undefined;
  resistanceFactor?: number | undefined;
  gasDerateMw?: number | undefined;
  ironLossMw?: number | undefined;
  // engine.v3 P9 (unit-commitment): this tick's startup / no-load $ (diagnostics) + committed / ramp-bound counts.
  // Always emitted (0 / count on the neutral path).
  startupCostDollars?: number | undefined;
  noLoadCostDollars?: number | undefined;
  committedUnits?: number | undefined;
  rampBoundUnits?: number | undefined;
  // engine.v3 P9-rewrite (H1 liveness): # units that wanted to decommit but the reserve vetoed. Always 0 on the
  // neutral path; >0 signals an over-committed fleet (units burning no-load cost they can't recover).
  decommitLatchedUnits?: number | undefined;
  // engine.v3 P10 (hydro reservoir): aggregate SoC + fleet water value (undefined when no reservoir units exist).
  hydroReservoirMwh?: number | undefined;
  hydroWaterValuePerMwh?: number | undefined;
  // engine.v3 P10-rewrite (D-SPILL): MW-equivalent water spilled this tick (inflow that overflowed a full reservoir).
  // Always-emitted 0 when no spill / no reservoir units (telemetry ONLY — NOT a power-balance term).
  hydroSpillMw?: number | undefined;
  // engine.v3 P15: L4 TOU-shift reservoir level + L5 Hz-ladder UFLS attribution. Always-emitted 0 (neutral).
  deferredMwh?: number | undefined;
  uflsShedMw?: number | undefined;
  drFactor: number;
  elasticConsumerBaseMw: number;
  settlementDollars: number;
  cumSettlementDollars: number;
  commsHealth: number;
  bessSocMwh: number;
  bessConversionLossMw: number; // F6.2
  openPorts: string[];
  displayOnlyNodes: readonly NodeId[];
  manualOffEdges: EdgeId[];
  faults: Fault[];
}

export class SnapshotAssembly {
  constructor(
    private readonly nodes: GridNode[],
    private readonly edges: GridEdge[],
    private readonly nodeIndex: ReadonlyMap<NodeId, number>,
  ) {}

  assemble(a: AssembleArgs): Snapshot {
    const { dr, freq, theta, drFactor } = a;
    // ONT full-integration: displayOnly transmission stations have no electrical path to generation (an
    // un-bridgeable OSM fragment) — they are rendered as static infrastructure, NOT counted as outages.
    const outages = this.nodes
      .filter((n) => !a.energized.has(n.id) && !n.displayOnly)
      .map((n) => n.id);
    let busAngleMaxDeg = 0;
    for (let i = 0; i < theta.length; i++) busAngleMaxDeg = Math.max(busAngleMaxDeg, Math.abs(theta[i]!) * RAD2DEG);
    let branchAngleMaxDeg = 0;
    const nodeAngles = new Map<NodeId, number>();
    this.nodes.forEach((n, i) => nodeAngles.set(n.id, theta[i]!));

    // engine.v3 P7 (C18 promoted): per-bus voltage magnitude vMagPu = √w from the LinDistFlow proxy — mirror
    // the nodeAngles index basis EXACTLY (same static node array) so bus k's voltage never lands on k+1. A
    // blacked-out / displayOnly bus carries w=0 ⇒ vMagPu 0 (like θ=0). The min/max scalars are taken over
    // ENERGIZED, non-displayOnly buses only (skip w===0), so a dark island can't drag minVMagPu to 0 and
    // make the scalar useless; sagBusCount is the count below the ANSI 0.95 pu service floor. On the neutral
    // path every energized bus ≈ 1.0 ⇒ min≈max≈1.0, sagBusCount=0 (golden byte-identical: voltage never
    // feeds θ/dispatch and the golden edges carry rPu=0 ⇒ w≡1 flat).
    const busVMagPu = new Map<NodeId, number>();
    let minVMagPu = Infinity;
    let maxVMagPu = -Infinity;
    let sagBusCount = 0;
    this.nodes.forEach((n, i) => {
      const w = a.voltageSq[i]!;
      const v = Math.sqrt(Math.max(0, w));
      busVMagPu.set(n.id, v);
      if (n.displayOnly || !a.energized.has(n.id)) return; // dark / static infra: not a service bus
      // sagBusCount counts EVERY energized bus at/below the 0.95 pu floor — including a v=0 linear-model
      // COLLAPSE (a deep radial feeder whose LinDistFlow w went ≤0 under heavy load: physically the worst sag).
      if (v < SAG_PU) sagBusCount++;
      // min/max are taken over ON-MODEL buses (v>0) so a single collapsed bus can't pin minVMagPu at exactly
      // 0 and read like a dead bus — the collapse is already reflected in sagBusCount + the 0 on busVMagPu.
      if (v > 0) {
        if (v < minVMagPu) minVMagPu = v;
        if (v > maxVMagPu) maxVMagPu = v;
      }
    });
    // no energized non-displayOnly bus (a fully-dark model) ⇒ report the reference 1.0, not ±Infinity.
    if (!Number.isFinite(minVMagPu)) { minVMagPu = 1; maxVMagPu = 1; }
    for (const e of this.edges) {
      // measure the DC-validity angle ONLY over IN-SERVICE, energized branches: the angle across an OPEN
      // switch / de-energized edge is unconstrained and meaningless. golden-safe: golden asserts per-bus
      // |Δθ| via nodeAngles, not this metric.
      if (a.openEdges.has(e.id)) continue;
      const iu = this.nodeIndex.get(e.u);
      const iv = this.nodeIndex.get(e.v);
      if (iu === undefined || iv === undefined) continue;
      if (!a.energized.has(e.u) || !a.energized.has(e.v)) continue;
      branchAngleMaxDeg = Math.max(branchAngleMaxDeg, Math.abs(theta[iu]! - theta[iv]!) * RAD2DEG);
    }
    let genMw = 0;
    for (const d of dr.dispatch) genMw += d.outputMw;
    // P16 C66: nuclear MUST be a subset of baseload (both include import nuclear like Bruce). Dev-guard only
    // (no-op in prod — a divergence is an engine bug, not a user-visible failure). The audit found import Bruce
    // booked into baseloadMw but excluded from nuclearMw; verify the partition is coherent here.
    if (dr.nuclearMw !== undefined && dr.baseloadMw !== undefined) {
      if (dr.nuclearMw > dr.baseloadMw + 0.1) {
        // eslint-disable-next-line no-console -- dev visibility only
        console.warn(`[P16 C66] nuclearMw ${dr.nuclearMw} > baseloadMw ${dr.baseloadMw} (partition divergence)`);
      }
    }

    // P12 (KS-DYN): unserved = TOTAL unmet demand across the load runtimes = topologically-DARK loads (full
    // demand, de-energized by a topology change) + UFLS supply-SHED loads (demand − served, from a contingency
    // that exceeded the operating reserve). This unifies BOTH chains-of-effect through the single load-status
    // ledger — the dispatch shortfall is ATTRIBUTED onto shed loads (the engine re-dispatches to the served
    // target, so dr.unservedMw is consumed), so the headline equals exactly what the map darkens, no
    // double-count. GOLDEN-NEUTRAL: the 5 anchors energize + fully serve every load ⇒ Σ(demand − served) = 0.
    let unservedMw = 0;
    for (const l of a.loads) unservedMw += Math.max(0, l.demandMw - l.servedMw);

    return {
      tick: a.tickCount,
      scenario: a.scenario,
      price: a.price,
      dispatch: dr.dispatch,
      branchFlows: a.branchFlows,
      nodeAngles,
      busVMagPu, // engine.v3 P7: per-bus √w (LinDistFlow), ~1.0 nominal, 0 on a blacked-out bus (C18 promoted)
      busAngleMaxDeg,
      branchAngleMaxDeg,
      totals: {
        grossDemandMw: a.grossDemandMw,
        netLoadMw: a.totalNetLoadMw,
        genMw,
        slackMw: dr.slackMw,
        importMw: dr.importMw,
        gtaGasMw: dr.gtaGasMw,
        curtailedMw: dr.curtailedMw,
        unservedMw, // dispatch shortfall + de-energized (dark) load demand from topology toggles
        reserveMw: dr.reserveMw,
        marginalCostPerMwh: dr.marginalCostPerMwh, // v3.1: system λ (0 in surplus hours); F2: VOLL-lifted under shed
        ...(dr.scarcityAdderPerMwh !== undefined ? { scarcityAdderPerMwh: dr.scarcityAdderPerMwh } : {}), // F2 (RC-3)
        nuclearMw: dr.nuclearMw, // v3.2: flat nuclear baseload band (incl. import nuclear)
        exportMw: dr.exportMw, // v3.2: surplus baseload exported over the interface
        // v4 (advanced dispatch, design §F): role stack bands + marginal unit + losses + emissions
        baseloadMw: dr.baseloadMw,
        loadFollowingMw: dr.loadFollowingMw,
        peakerMw: dr.peakerMw,
        storageNetMw: dr.storageNetMw,
        hydroReservoirDischargeMw: dr.hydroReservoirDischargeMw, // engine.v3 P10: reservoir_hydro band (0 legacy)
        vreMw: dr.vreMw,
        lossMw: a.lossMw,
        // engine.v3 P11: distribution loss (modeled per-load) + combined T&D total (always-emitted).
        distLossMw: a.distLossMw ?? 0,
        totalTndLossMw: a.totalTndLossMw ?? a.lossMw,
        // engine.v3 P12: protection coordination telemetry (always 0 on N-0; golden-neutral).
        lockedOutCount: a.lockedOutCount ?? 0,
        protectionUntripCount: a.protectionUntripCount ?? 0,
        emissionsKg: dr.emissionsKg,
        ...(dr.marginalUnitId !== undefined ? { marginalUnitId: dr.marginalUnitId } : {}),
        ...(dr.marginalFuel !== undefined ? { marginalFuel: dr.marginalFuel } : {}),
        ...(dr.marginalNetworkRole !== undefined ? { marginalNetworkRole: dr.marginalNetworkRole } : {}),
        // engine.v2 System-Dynamics frequency core (design §2,§4): COI frequency, RoCoF, nadir,
        // fleet inertia, ACE, deployed primary reserve. 60 Hz / 0 at steady state (golden-neutral).
        frequencyHz: freq.frequencyHz,
        rocofHzPerS: freq.rocofHzPerS,
        inertiaMwS: freq.inertiaMwS,
        inertiaConstantS: freq.inertiaConstantS,
        aceMw: freq.aceMw,
        freqNadirHz: freq.freqNadirHz,
        primaryReserveMw: freq.primaryReserveMw,
        // P2: BESS fast-frequency-response peak + aggregate reservoir (post-depletion).
        bessFfrMw: freq.bessFfrMw,
        bessSocMwh: a.bessSocMwh,
        bessConversionLossMw: a.bessConversionLossMw, // F6.2: √η round-trip loss this tick (0 at golden)
        // engine.v3 P10: reservoir hydro SoC + fleet water value (undefined ⇒ omitted when no reservoir units).
        ...(a.hydroReservoirMwh !== undefined ? { hydroReservoirMwh: a.hydroReservoirMwh } : {}),
        ...(a.hydroWaterValuePerMwh !== undefined ? { hydroWaterValuePerMwh: a.hydroWaterValuePerMwh } : {}),
        hydroSpillMw: a.hydroSpillMw ?? 0,
        // engine.v3 P15: L4 TOU-shift reservoir + L5 UFLS attribution — always-emitted neutral 0.
        deferredMwh: a.deferredMwh ?? 0,
        uflsShedMw: a.uflsShedMw ?? 0,
        // engine.v3 P16: the coherence totals computed ONCE (C67/C68/C84) so charts + headline never diverge.
        // C67: honest supply margin = gen − netLoad − loss − export (NOT gen−netLoad which conflates loss+export).
        supplyMarginMw: genMw - a.totalNetLoadMw - a.lossMw - (dr.exportMw ?? 0),
        // C68: the "other" gen band = Math.max(0, gen − nuclear − gas − import), single clamp source.
        otherGenMw: Math.max(0, genMw - (dr.nuclearMw ?? dr.slackMw) - dr.gtaGasMw - dr.importMw),
        // C84: transient vs steady discriminator — plot nadir/RoCoF only on a disturbance tick.
        isDisturbance: freq.rocofHzPerS !== 0 || Math.abs(60 - freq.freqNadirHz) > 0.01 || a.faults.length > 0,
        // P3: price-elastic demand response APPLIED this tick (drFactor−1 = the prior-tick stock that
        // shaped this tick's consumer demand). demandResponseMw>0 = curtailed, <0 = rebound.
        demandResponseFrac: drFactor - 1,
        // >0 curtailed (energized-consumer base); `|| 0` normalises −0 (rebound × zero base) to +0.
        demandResponseMw: (1 - drFactor) * a.elasticConsumerBaseMw || 0,
        // P4: comms-layer health (0 during a cyberattack, ramps to 1 in recovery) + finance settlement.
        commsHealthFrac: a.commsHealth,
        settlementDollars: a.settlementDollars,
        cumulativeSettlementDollars: a.cumSettlementDollars,
        // engine.v3 P0-harness: the physics self-check diagnostics (Σ P[i] and the derived slack).
        ...(a.conservationResidualMw !== undefined ? { conservationResidualMw: a.conservationResidualMw } : {}),
        ...(a.slackAbsorbedMw !== undefined ? { slackAbsorbedMw: a.slackAbsorbedMw } : {}),
        // engine.v3 P2 (domain-v5): the full per-tick InvariantReport (rides the packer-by-exclusion wire).
        ...(a.invariant !== undefined ? { invariant: a.invariant } : {}),
        // engine.v3 P5 (domain-v6): live MSSC (reserve sizing) — ALWAYS emitted (domain.v6 contract; the P5
        // engine always computes it, ts-engine.ts:344), matching the P7 always-emit sibling below. An older
        // caller / test literal that omits it ⇒ 0 (a neutral "no contingency" MSSC), never dropped. audit fix
        // 2026-07-03: the earlier `!== undefined` guard contradicted the "always emitted" doc.
        largestContingencyMw: a.largestContingencyMw ?? 0,
        // the SCED binding diagnostics stay conditional — they are genuinely optional (present only when a
        // corridor/branch actually binds), unlike the always-sized MSSC above.
        ...(a.bindingCorridors !== undefined && a.bindingCorridors.length > 0 ? { bindingCorridors: a.bindingCorridors } : {}),
        ...(a.bindingBranchCount !== undefined && a.bindingBranchCount > 0 ? { bindingBranchCount: a.bindingBranchCount } : {}),
        // engine.v3 P7 (domain-v7 — LinDistFlow voltage): always-emit min/max √w over energized non-displayOnly
        // buses + the count below the ANSI 0.95 pu sag floor. ≈1.0/1.0/0 on the neutral path (golden byte-identical).
        minVMagPu, maxVMagPu, sagBusCount,
        // engine.v3 P8 — env→infrastructure telemetry, ALWAYS emitted (neutral 1.0 / 0 at/below the references).
        lineRatingFactor: a.lineRatingFactor ?? 1, resistanceFactor: a.resistanceFactor ?? 1,
        gasDerateMw: a.gasDerateMw ?? 0, ironLossMw: a.ironLossMw ?? 0,
        // engine.v3 P9 — unit-commitment telemetry, ALWAYS emitted (0 $ / count on the neutral path).
        startupCostDollars: a.startupCostDollars ?? 0, noLoadCostDollars: a.noLoadCostDollars ?? 0,
        committedUnits: a.committedUnits ?? 0, rampBoundUnits: a.rampBoundUnits ?? 0,
        decommitLatchedUnits: a.decommitLatchedUnits ?? 0, // P9-rewrite H1: always 0 on the neutral path
      },
      flows: [...a.energyFlows, ...a.overlayFlows, ...a.financeFlows],
      outages,
      displayOnlyNodes: a.displayOnlyNodes, // P4: static infrastructure — UI labels these honestly
      manualOffEdges: a.manualOffEdges, // P-viz: lines taken out of service (UI: Restore vs Turn off)
      openPorts: a.openPorts, // REV-B: per-connection breakers the operator/data opened ([] = all closed)
      faultsActive: a.faults,
      prosumers: a.prosumers,
      loads: a.loads,
      meritStack: dr.meritStack, // P13: the cost-sorted supply stack (names the price-setter + the supply curve)
      ...(a.nodalPrices !== undefined ? { nodalPrices: a.nodalPrices } : {}), // F4b (P13): per-bus LMP (gated on congestion)
    };
  }
}
